<template>
  <div>
    <NavMenu />
    <div class="page-content">

      <div v-if="busy"><BusySpinner /></div>

      <div v-if="!busy">
        <div class="searchbox">
          <input type="text" placeholder="Order or Transaction Number" v-model="query" v-on:keypress.enter="doSearch" />
          <button v-on:click="doSearch">Search</button>
        </div>
        <div class="error-text" v-if="errorMsg">
            Error: {{ errorMsg }}
        </div>

        <div class="order-info" v-if="orderInfo.status == 'success'">
            <div class="infocards">
              <div class="infocard" v-bind:class="{grayed: !order.raw.valid}" v-for="order in filteredOrders" :key="order.orderNumber">
                <h1>{{order.orderNumber}} ({{order.raw.store}})</h1>
                <table>
                  <tr><td>SKU</td><td>{{order.raw.sku}} ({{order.raw.productInfo ? order.raw.productInfo.type : ''}})</td></tr>
                  <tr><td>Purchased</td><td>{{order.purchaseTime}}</td></tr>
                  <tr v-if="!order.consumable"><td>Canceled</td><td>{{order.cancelTime}}</td></tr>
                  <tr v-if="!order.consumable"><td>Expires</td><td v-bind:class="{expired: !order.valid}">{{order.expires}}</td></tr>
                  <tr><td>Last Update</td><td>{{order.lastUpdate}}</td></tr>
                  <tr v-if="!order.consumable"><td>Auto-Renewing</td><td>{{order.raw.autoRenewing}}</td></tr>
                  <tr><td>Country Code</td><td>{{order.raw.countryCode}}</td></tr>
                  <tr v-if="order.consumable"><td>Applied</td><td>{{order.applied}}</td></tr>
                  <tr v-if="!order.consumable"><td>Cancel Reason</td><td>{{order.raw.cancelReason}}</td></tr>
                  <tr><td>User ID</td><td>
                    <div v-for="u in order.uids" :key="u.uid">
                      <router-link v-bind:to="u.link"><span class="fixed"><span v-for="p in u.uidParts" :key="p">{{p}}</span></span></router-link>
                      <span v-if="u.linked"> (linked)</span>
                      <button v-else @click="linkOrder(u.uid, order.orderNumber, order.raw.store === 'App Store')" style="cursor: pointer"> Link this UID</button>
                    </div>
                    <div>
                      <input v-model="newUID" type="text" size="40" placeholder="New User ID" />
                      <button @click="linkOrder(null, order.orderNumber, order.raw.store === 'App Store')" style="cursor: pointer; margin-left: 10pt;"> Link new UID</button>
                    </div>
                  </td></tr>
                  <tr><td>
                    <button @click="refreshSubscription(order.raw.store !== 'Google Play', order.orderNumber)" class="refresh-sub">
                      Refresh Subscription State 
                    </button>
                  </td></tr>
                </table>
                <details><summary>Raw JSON</summary><pre class="raw">{{order.rawText}}</pre></details>
              </div>
            </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>

import NavMenu from '@/components/NavMenu.vue'
import BusySpinner from '@/components/BusySpinner.vue'

import firebase from 'firebase/app'
import 'firebase/functions'

let lookupOrderInfo = firebase.functions().httpsCallable('lookupOrderInfo')

let dateFormat = Intl.DateTimeFormat(undefined,{
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
  timeZone: 'Asia/Seoul',
  timeZoneName: "short"
})

export default {
  name: 'Orders',
  components: { NavMenu, BusySpinner },
  data: ()=>({
    busy: false,
    query: "",
    resultQuery: "",
    newUID: "",
    orderInfo: {},
    errorMsg: ''
  }),
  computed: {
    filteredOrders() {
      return this.orderInfo.orders.map(order=>{
        let uids = [];
        let linkedUid = order.linkedUid;
        let foundLinked = false;

        if( order.relatedUids ) {
          order.relatedUids.forEach(uid=>{
            if(uid==linkedUid) {
              foundLinked = true;
            }
            uids.push({uid: uid, linked: uid==linkedUid, uidParts: uid.match(/.{1,7}/g), link: `/user/${uid}` })
          })
        }

        if( linkedUid && !foundLinked ) {
            uids.push({uid: linkedUid, linked: true, uidParts: linkedUid.match(/.{1,7}/g), link: `/user/${linkedUid}` })
        }

        return {
          orderNumber: order.orderNumber,
          purchaseTime: order.purchaseTimeMillis ? dateFormat.format(new Date(parseInt(order.purchaseTimeMillis,10))) : "",
          cancelTime: order.cancelTimeMillis ? dateFormat.format(new Date(parseInt(order.cancelTimeMillis,10))) : "",
          lastUpdate: order.lastUpdate ? dateFormat.format(new Date(parseInt(order.lastUpdate,10))) : "",
          expires: order.productInfo?.type == "consumable" ? "n/a/ (consumable)" : order.expiresTimeMillis ? dateFormat.format(new Date(parseInt(order.expiresTimeMillis,10))) : "",
          valid: order.expires >= Date.now(),
          uids: uids,
          raw: order,
          applied: order.processedTickets,
          consumable: order.productInfo?.type == "consumable",
          rawText: JSON.stringify(order,null,4)
        }
      });
    }
  },
  beforeRouteUpdate (to, from, next) {
    let routeOid = to.params.oid;
    console.log(`beforeRouteUpdate: ${routeOid} ==? ${to.params.oid} ${routeOid != this.resultQuery}`)
    if( routeOid != this.resultQuery ) {
      this.executeQuery(routeOid)
    }
    next()
  },
  beforeRouteEnter (to, from, next) {
    next(vm=>{
      let routeOid = to.params.oid;
      console.log(`beforeRouteUpdate: ${routeOid} ==? ${vm.resultQuery} ${routeOid != vm.resultQuery}`)
      if( routeOid != vm.resultQuery ) {
        vm.executeQuery(routeOid)
      }
      next()
    })
  },
  methods: {
    checkEmulator() {
      const curl = window.location.href
      if (curl.includes("localhost") || curl.includes("0.0.0.0")) {
          firebase.functions().useFunctionsEmulator("http://0.0.0.0:5001")
      }
    },
    refreshSubscription(isApple, transactionID) {
      this.checkEmulator()
      const endpoint = isApple ? firebase.functions().httpsCallable('refreshSubscriptionApple') : firebase.functions().httpsCallable('refreshSubscriptionGoogle')
      this.busy = true
      endpoint({transactionID}).then((res) => {
        this.busy = false
        switch (res.data.status) {
          case "not-found":
              alert(res.data.comment)
          break
          case "success":
              this.response = res.data.comment
          break
          case "error":
              alert(res.data.comment)
          break
        }
      }).catch((error) => {
        this.busy = false
        alert(error)
      })
    },
    doSearch() {
      this.executeQuery(this.query);
    },
    executeQuery(query) {
      var page = this;
      page.orderInfo = {}
      page.busy = true;
      if(query) {
        lookupOrderInfo({search: query}).then(function(result){
          page.busy = false
          const status = result.data.status
          if (status === 'no-permission') {
            page.errorMsg = 'No permission'
            return 
          }
          if (!result.data.orders?.length) {
            page.errorMsg = 'Order information not found'
            return
          }
          if (page.$route.params.oid != query) {
            page.$router.push({name:"Order", params:{oid: query}})
          }
          page.orderInfo = result.data
          page.resultQuery = query
          console.log("RESULT",result.data)
          page.errorMsg = ''
        }).catch(function(/*error*/){
          page.busy = false
          page.orderInfo = {}
        })
      } else {
        page.busy = false;
      }
    },
    linkOrder(existingUID, oid, isApple) {
      let uid = existingUID
      this.checkEmulator()
      const endpoint = firebase.functions().httpsCallable('changePrimaryUID')
      this.busy = true
      if (this.newUID) {
        uid = this.newUID
        this.newUID = ''
      }
      endpoint({uid, oid, isApple}).then((res) => {
        switch (res.data.status) {
          case "not-found":
              alert(`User ID: ${res.data.comment}, not found error.`)
          break
          case "success":
              this.response = res.data.comment
              this.executeQuery(oid)
          break
          case "no-permission":
              alert('No permission')
          break
          case "error":
              alert(res.data.comment)
          break
        }
        this.busy = false
      }).catch((error) => {
        this.busy = false
        alert(error)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.error-text {
  color: red;
  font-weight: bold;
}

.searchbox {
  border-bottom: 1px solid #AAA;
  padding-left: 10pt;
  padding-right: 10pt;
  padding-bottom: 8pt;
  margin-left: -10pt;
  margin-right: -10pt;

  input {
    width: calc( 100% - 90pt );
    font-size: 13pt;
    border: 1px solid #BBB;
    border-radius: 2pt;
    padding: 3pt;
  }

  input:focus-visible {
    border: 2px solid rgb(116, 114, 148);
  }

  input::placeholder {
    color: #BBB;
  }

  button {
    font-size: 12pt;
    width: 60pt;
    margin-left: 5pt;
    padding: 3pt;
  }
}

.order-info {

  margin-left: 10pt;
  margin-right: 10pt;
  margin-top: 20pt;

  h1 {
    font-size: 16pt;
    margin-top: 30pt;
    margin-bottom: 5pt;
  }

  .raw {
    font-family: 'Courier New', Courier, monospace;
    font-size: 10pt;
  }

  .refresh-sub {
    margin: 10px 0px;
    width: 200px;
    padding: 5px;
    cursor: pointer;
  }

  .infocards {
    margin-top: 0pt;

    .infocard.grayed {
      color: #AAA;
      table { tr { td:first-of-type { color:#AAA }}}
    }

    .fixed {
      font-family: 'Courier New', Courier, monospace;
      span {
        display: inline-block;
        margin-right: 5pt;
      }
    }

    summary {
      cursor: pointer;
      margin-top: 6pt;
      margin-left: 5pt;
      font-size: 10pt;
      outline: none;
    }

    summary:active {
      color: #888;
    }

    summary:focus {
      text-decoration: underline;
      text-decoration-style: dotted;
    }

    .infocard {
      display: block;
      margin-top: 20pt;
      border: 1px solid #AAA;
      border-radius: 8pt;
      margin-bottom: 10pt;
      min-height: 20pt;
      padding-left: 5pt;
      padding-right: 5pt;
      padding-top: 10pt;
      padding-bottom: 7pt;

      h1 {
        position: absolute;
        margin-top: -18pt;
        margin-bottom: 0pt;
        margin-left: 6pt;
        font-size: 12pt;
        background: #FFF;
        display: inline-block;
        padding-left: 4pt;
        padding-right: 4pt;
      }

      table {
        // border: 1px solid #EEE;
        border-collapse: collapse;
        width: 100%;

        tr {
          border-top: 1px solid #EEE;
          border-bottom: 1px solid #EEE;

          td {
            padding-top: 2pt;
            padding-bottom: 2pt;
          }

          td:first-of-type {
            font-size: 8pt; 
            width: 65pt;
            color: #555;
            padding-left: 3pt;
          }


          td:nth-of-type(2) {
            font-size: 10pt; 
            padding-left: 10pt;
          }
        }
        .expired {
          color: #F00;
        }
      }

      .benefit {
        display: inline-block;
        margin-right: 5pt;
        padding: 2pt;
        background-color: #EEE;
        border-radius: 12pt;
        padding-left: 8pt;
        padding-right: 8pt;
        font-size: 8pt;
        font-weight: bold;
      }

      .active .benefit {
        background-color: rgb(170, 218, 195);
      }

    }
  }
}

</style>
